import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Download, Monitor, Apple, Laptop } from "lucide-react";
import { SiLinux, SiApple, SiRedhat } from "react-icons/si";
import { useToast } from "../../../hooks/use-toast";
import { useEffect, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../APIs";
import useUserStore from "../../../services/userStore";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";

export default function DownloadPage() {
    const { toast } = useToast();
    const [windowsVersion, setWindowsVersion] = useState(null);
    const [linuxVersion, setLinuxVersion] = useState(null);
    const [macVersion, setMacVersion] = useState(null);


    const downloadLinks = {
        windows: `${NGROK}/api/download`,
        mac: `${NGROK}/api/download/macos`,
        linuxDeb: `${NGROK}/api/download/ubuntu`,
        linuxRpm: `${NGROK}/api/download/rhel`,
    };

    const handleDownload = (platform: string, packageType: string, url: string) => {
        // Trigger download in new tab
        window.open(url, "_blank", "noopener,noreferrer");

        // Show toast
        toast({
            title: "Download Started",
            description: `WhiteSwanSecurity ${packageType} for ${platform} download has been initiated.`,
        });
    };




    const callForWindows = async () => {
        let url = `${NGROK}/api/agent/windows-agent-version`;

        const response = await axios.get(url);
        if (response?.data) {
            setWindowsVersion(response?.data);
        }
    };
    const callForLinux = async () => {
        let url = `${NGROK}/api/agent/linux-agent-version`;

        const response = await axios.get(url);
        if (response?.data) {
            setLinuxVersion(response?.data);
        }
    };
    const callForMac = async () => {
        let url = `${NGROK}/api/agent/mac-agent-version`;

        const response = await axios.get(url);
        if (response?.data) {
            setMacVersion(response?.data);
        }
    };
    useEffect(() => {
        callForWindows();
        callForLinux();
        callForMac();
    }, []);


    return (
        <div className="min-h-full bg-background">
            {/* Header with Branding */}
            <div className="bg-slate-800 dark:bg-slate-900 py-8 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                            <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
                                <span className="text-white font-bold text-sm">WS</span>
                            </div>
                        </div>
                        <div className="text-white">
                            <h1 className="text-2xl font-bold">WHITESWAN</h1>
                            <p className="text-sm text-slate-300 uppercase tracking-wider">IDENTITY SECURITY</p>
                        </div>
                    </div>
                    <p className="text-slate-300 max-w-2xl mx-auto">
                        Download the WhiteSwanSecurity agent to onboard and secure your devices across all platforms
                    </p>
                </div>
            </div>

            {/* Download Cards */}
            <div className="max-w-6xl mx-auto px-6 py-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                    {/* Windows */}
                    <Card className="group hover-elevate transition-all duration-200">
                        <CardContent className="p-8 text-center">
                            <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                                <FaWindows className="w-16 h-16 text-blue-600" />
                            </div>

                            <h3 className="text-xl font-semibold mb-2">Windows</h3>
                            <p className="text-sm text-muted-foreground mb-6">(v.{windowsVersion})</p>

                            <Button
                                onClick={() => handleDownload("Windows", "MSI", downloadLinks.windows)}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid="download-windows-msi"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Download WhiteSwanSecurity MSI
                            </Button>

                            <div className="mt-4 pt-4 border-t">
                                <p className="text-xs text-muted-foreground">
                                    Compatible with Windows 10/11, Windows server
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Requires Administrator privileges
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Mac */}
                    <Card className="group hover-elevate transition-all duration-200">
                        <CardContent className="p-8 text-center">
                            <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                                <FaApple className="w-16 h-16 text-gray-700 dark:text-gray-300" />
                            </div>

                            <h3 className="text-xl font-semibold mb-2">Mac</h3>
                            <p className="text-sm text-muted-foreground mb-6">(v.{macVersion})</p>

                            <Button
                                onClick={() => handleDownload("Mac", "PKG", downloadLinks.mac)}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid="download-mac-pkg"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Download WhiteSwanSecurity PKG
                            </Button>

                            <div className="mt-4 pt-4 border-t">
                                <p className="text-xs text-muted-foreground">
                                    Compatible with macOS 11+
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Requires Administrator privileges
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Linux */}
                    <Card className="group hover-elevate transition-all duration-200">
                        <CardContent className="p-8 text-center">
                            <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                                <FaLinux className="w-16 h-16 text-black dark:text-white" />
                            </div>

                            <h3 className="text-xl font-semibold mb-2">Linux</h3>
                            <p className="text-sm text-muted-foreground mb-6">(v.{linuxVersion})</p>

                            <div className="space-y-3">
                                <Button
                                    onClick={() => handleDownload("Linux", "DEB", downloadLinks.linuxDeb)}
                                    variant="outline"
                                    className="w-full bg-blue-600 text-white group-hover:border-blue-600"
                                    data-testid="download-linux-deb"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download WhiteSwanSecurity DEB
                                </Button>

                                <Button
                                    onClick={() => handleDownload("Linux", "RPM", downloadLinks.linuxRpm)}
                                    variant="outline"
                                    className="w-full bg-blue-600 text-white group-hover:border-blue-600"
                                    data-testid="download-linux-rpm"
                                >
                                    <SiRedhat className="w-4 h-4 mr-2" />
                                    Download WhiteSwanSecurity RPM
                                </Button>
                            </div>

                            <div className="mt-4 pt-4 border-t">
                                <p className="text-xs text-muted-foreground">
                                    DEB: Ubuntu, Debian
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    RPM: RHEL, CentOS, Fedora
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Installation Instructions */}
                <div className="mt-16">
                    <Card>
                        <CardContent className="p-8">
                            <h2 className="text-2xl font-semibold mb-6 text-center">Installation Instructions</h2>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="text-center">
                                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                        <FaWindows className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <h3 className="font-semibold mb-3">Windows Installation</h3>
                                    <ol className="text-sm text-muted-foreground space-y-2 text-left">
                                        <li>1. Download the MSI installer</li>
                                        <li>2. Run as Administrator</li>
                                        <li>3. Follow setup wizard</li>
                                        <li>4. Agent will start automatically</li>
                                    </ol>
                                </div>

                                <div className="text-center">
                                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                        <FaApple className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                                    </div>
                                    <h3 className="font-semibold mb-3">Mac Installation</h3>
                                    <ol className="text-sm text-muted-foreground space-y-2 text-left">
                                        <li>1. Download the PKG installer</li>
                                        <li>2. Double-click to open</li>
                                        <li>3. Enter admin password</li>
                                        <li>4. Complete installation process</li>
                                    </ol>
                                </div>

                                <div className="text-center">
                                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                        <FaLinux className="w-6 h-6 text-black dark:text-white" />
                                    </div>
                                    <h3 className="font-semibold mb-3">Linux Installation</h3>
                                    <ol className="text-sm text-muted-foreground space-y-2 text-left">
                                        <li>1. Download appropriate package</li>
                                        <li>2. Install with package manager</li>
                                        <li>3. Configure service settings</li>
                                        <li>4. Complete installation process</li>
                                    </ol>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>


            </div>
        </div>
    );
}